// src/components/SearchBar/SearchBar.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE } from "../../services/api";

export default function SearchBar({ className = "" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const debounce = useRef();

  // --- Debounce-haku ---
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/search?q=${encodeURIComponent(query)}&limit=12`
        );
        const data = await res.json();
        if (Array.isArray(data?.items)) {
          setResults(data.items.slice(0, 12));
        } else {
          setResults([]);
        }
      } catch (e) {
        console.warn("[SearchBar] haku epäonnistui:", e);
        setResults([]);
      }
    }, 250);

    return () => clearTimeout(debounce.current);
  }, [query]);

  // --- Klikki ulos → sulje dropdown ---
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleSelect = (item) => {
    setOpen(false);
    setQuery("");

    const origin = window.location.origin.replace(/\/+$/, "");

    console.log("[SearchBar] selected item:", item);

    // 1) Esiintyjät → rakennetaan itse URL, ei käytetä React Routeria
    if (item.type === "band" && item.slug) {
      const path = `/esiintyja/${item.slug}`.replace(/\/+/, "/");
      window.location.href = `${origin}${path}`;
      return;
    }

    // 1b) Blogipostit → /blogi/:slug
    if (item.type === "post") {
      // WP search ei anna slugia → johdetaan se URL:sta jos puuttuu
      let slug = item.slug;
      if (!slug && item.url) {
        try {
          const u = new URL(item.url);
          const parts = u.pathname.replace(/\/+$/, "").split("/");
          slug = parts[parts.length - 1] || "";
        } catch {
          slug = "";
        }
      }

      if (slug) {
        const path = `/blogi/${slug}`.replace(/\/+/, "/");
        window.location.href = `${origin}${path}`;
        return;
      }
      // jos slugia ei kuitenkaan saada → pudotaan WP-URL-haaraan alempana
    }

    // 2) WP-sisällöt (page tms.)
    let rawUrl = (item.url || "").trim();
    if (!rawUrl) return;

    // Poista mahdolliset näkymättömät/zero-width -merkit alusta
    rawUrl = rawUrl.replace(/^[\s\u200B\u200C\u200D\uFEFF]+/, "");

    // Normalisoi mahdollinen "https://buukkeri.fi/https://buukkeri.fi/..."
    rawUrl = rawUrl.replace(
      /^https?:\/\/(?:www\.)?buukkeri\.fi\/https?:\/\/(?:www\.)?buukkeri\.fi\//i,
      "https://buukkeri.fi/"
    );

    // Jos merkkijono sisältää HTTP(S)-protokollan missä tahansa kohtaa,
    // käsitellään sitä absoluuttisena URL:na
    if (/https?:\/\//i.test(rawUrl)) {
      window.location.href = rawUrl;
      return;
    }

    // Muuten oletetaan relatiiviseksi poluksi
    const path = rawUrl[0] === "/" ? rawUrl : `/${rawUrl}`;
    window.location.href = `${origin}${path}`;
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 bg-white/80 dark:bg-black/40 backdrop-blur-md rounded-xl border border-black/10 dark:border-white/10 px-3 py-1 shadow-sm">
        <Search className="h-4 w-4 text-black/60 dark:text-white/60" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Etsi…"
          className="bg-transparent flex-1 outline-none text-sm text-black dark:text-white placeholder:text-black/40 dark:placeholder:text-white/40"
        />
      </div>

      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 mt-1 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-xl z-50 max-h-80 overflow-y-auto"
          >
            {results.map((item) => (
              <li
                key={`${item.type}-${item.id || item.slug}`}
                onClick={() => handleSelect(item)}
                className="px-4 py-2 text-sm cursor-pointer hover:bg-pink/10 dark:hover:bg-electric/20"
              >
                <div className="font-semibold text-black dark:text-white truncate">
                  {item.title}
                </div>
                <div className="mt-0.5 text-xs text-black/60 dark:text-white/60 truncate">
                  {item.type === "band"
                    ? item.subtitle || "esiintyjä"
                    : item.type === "post"
                    ? "blogi"
                    : "sivu"}
                </div>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
